import React, { useEffect, useRef } from 'react'
import { Spin } from 'antd'

export interface InfiniteScrollWrapperProps {
  /** Current page number (1-based) */
  currentPage: number
  /** Total pages available */
  totalPages: number
  /** Whether more items are currently being fetched */
  isFetchingMore: boolean
  /** Called when the user scrolls near the bottom and more data should be loaded */
  onLoadMore: (page: number) => void
  /**
   * The scrollable container element to attach the scroll listener to.
   * Use this when the scrollable container is rendered outside the wrapper
   * (e.g. inside a virtual list popup / portal).
   * When omitted, the wrapper's own root `<div>` is used as the scroll target.
   */
  scrollContainerEl?: HTMLElement | null
  children: React.ReactNode
}

/**
 * A generic infinite scroll wrapper that triggers `onLoadMore` when the user
 * scrolls near the bottom of the scrollable container.
 *
 * When `scrollContainerEl` is provided, it is used as the scroll target.
 * Otherwise the wrapper walks up from its root `<div>` to find the first
 * scrollable ancestor — useful for Ant Design virtual list popups.
 *
 * @example
 * // Auto-detect scrollable ancestor:
 * <InfiniteScrollWrapper currentPage={page} totalPages={total} isFetchingMore={fetching} onLoadMore={loadMore}>
 *   {items}
 * </InfiniteScrollWrapper>
 */
const InfiniteScrollWrapper: React.FC<InfiniteScrollWrapperProps> = ({
  currentPage,
  totalPages,
  isFetchingMore,
  onLoadMore,
  scrollContainerEl,
  children,
}) => {
  const wrapperRef = useRef<HTMLDivElement>(null)

  useEffect(() => {
    if (!(scrollContainerEl || wrapperRef.current)) return
    const scrollableEl = scrollContainerEl || wrapperRef.current as HTMLElement;

    const handler = () => {
      const { scrollHeight, scrollTop, clientHeight } = scrollableEl
      if (
        scrollHeight - scrollTop <= clientHeight + 20 &&
        !isFetchingMore &&
        currentPage < totalPages
      ) {
        onLoadMore(currentPage + 1)
      }
    }

    scrollableEl.addEventListener('scroll', handler, { passive: true })
    return () => scrollableEl.removeEventListener('scroll', handler)
  }, [currentPage, totalPages, isFetchingMore, onLoadMore, scrollContainerEl])

  return (
    <div ref={wrapperRef}>
      {children}
      {isFetchingMore && (
        <div style={{ textAlign: 'center', padding: '8px' }}>
          <Spin size="small" />
        </div>
      )}
    </div>
  )
}

export default InfiniteScrollWrapper